import React from 'react';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import {
  Collapse,
  IconButton,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from '@mui/material';
import {
  formatSmsUsagePrefix,
  getHighestUsagePercent,
  getSmsUsageBusinessKey,
} from 'utils/smsUsage/smsUsage';
import { SmsUsageBusiness, UpdateSmsUsageBlockPayload } from 'types/smsUsage/smsUsage';

export const SMS_USAGE_BUSINESS_COLUMNS = 5;

const formatAverage = (value: number): string =>
  value.toLocaleString('en-US', { maximumFractionDigits: 1 });

interface SmsUsageBusinessRowProps {
  business: SmsUsageBusiness;
  isExpanded: boolean;
  onToggleExpanded: () => void;
  /** False for a past day or while a change is saving; an unknown business is never blockable. */
  canChangeBlocks: boolean;
  onBlockChange: (payload: UpdateSmsUsageBlockPayload) => void;
}

const SmsUsageBusinessRow: React.FC<SmsUsageBusinessRowProps> = ({
  business,
  isExpanded,
  onToggleExpanded,
  canChangeBlocks,
  onBlockChange,
}) => {
  const { businessId, businessName, sent, blocked, prefixes } = business;
  const key = getSmsUsageBusinessKey(business);
  const highestUsagePercent = getHighestUsagePercent(prefixes);
  const isBlockable = canChangeBlocks && businessId !== null;

  return (
    <>
      <TableRow className="sms-usage-business-row" data-testid={`business-${key}`}>
        <TableCell padding="checkbox">
          <IconButton
            size="small"
            aria-label={`${isExpanded ? 'Collapse' : 'Expand'} business ${key}`}
            aria-expanded={isExpanded}
            onClick={onToggleExpanded}
          >
            {isExpanded ? <KeyboardArrowDownIcon /> : <KeyboardArrowRightIcon />}
          </IconButton>
        </TableCell>
        <TableCell>
          {businessId === null ? (
            'Unknown business'
          ) : (
            <>
              <span className="sms-usage-business-name">{businessName}</span>{' '}
              <span className="sms-usage-business-id">(ID {businessId})</span>
            </>
          )}
        </TableCell>
        <TableCell align="right">{sent}</TableCell>
        <TableCell align="right">
          {highestUsagePercent === null ? '—' : `${highestUsagePercent}%`}
        </TableCell>
        <TableCell>
          {businessId === null ? (
            <span className="sms-usage-muted">Not available</span>
          ) : (
            <Switch
              checked={blocked}
              disabled={!isBlockable}
              onChange={(event) => onBlockChange({ businessId, blocked: event.target.checked })}
              inputProps={{ 'aria-label': `Block business ${key}` }}
            />
          )}
        </TableCell>
      </TableRow>
      <TableRow className="sms-usage-prefixes-row">
        <TableCell colSpan={SMS_USAGE_BUSINESS_COLUMNS}>
          <Collapse in={isExpanded} timeout="auto" unmountOnExit>
            {prefixes.length === 0 ? (
              <p className="sms-usage-muted sms-usage-no-prefixes">No messages sent this day</p>
            ) : (
              <Table size="small" aria-label={`Prefixes of business ${key}`}>
                <TableHead>
                  <TableRow>
                    <TableCell>Prefix</TableCell>
                    <TableCell align="right">Sent</TableCell>
                    <TableCell align="right">Average</TableCell>
                    <TableCell align="right">Limit</TableCell>
                    <TableCell align="right">% used</TableCell>
                    <TableCell>Block prefix</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {prefixes.map((usage) => (
                    <TableRow key={usage.prefix} data-testid={`prefix-${key}-${usage.prefix}`}>
                      <TableCell>{formatSmsUsagePrefix(usage.prefix)}</TableCell>
                      <TableCell align="right">{usage.sent}</TableCell>
                      <TableCell align="right">{formatAverage(usage.average)}</TableCell>
                      <TableCell align="right">{usage.limit}</TableCell>
                      <TableCell align="right">{`${usage.usagePercent}%`}</TableCell>
                      <TableCell>
                        <Switch
                          checked={usage.blocked}
                          disabled={!isBlockable}
                          onChange={(event) => {
                            if (businessId !== null) {
                              onBlockChange({
                                businessId,
                                prefix: usage.prefix,
                                blocked: event.target.checked,
                              });
                            }
                          }}
                          inputProps={{
                            'aria-label': `Block prefix ${usage.prefix} of business ${key}`,
                          }}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

export default SmsUsageBusinessRow;
